/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useOutletContext } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiUrl } from '../utilities/apiUrl';
import { Box, Tab, Tabs, AppBar } from '@mui/material';
import {
  TimesheetTable,
  CutOffTable,
  PageContainer,
  CustomRangeModal,
} from '../components/index';
import '../css/timesheet.css';
import { subMonths, subYears, startOfMonth, endOfMonth } from 'date-fns';

export const Timesheet = (props) => {
  const [empId] = useOutletContext();
  const [timesheetList, setTimesheetList] = useState([]);
  const [timesheetCutoff, setTimesheetCutoff] = useState([]);
  const [value, setValue] = useState(
    parseInt(localStorage.getItem('tab')) ?? 0
  );
  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [startValue, setStartValue] = useState(null);
  const [endValue, setEndValue] = useState(null);
  const [preDefinedValue, setPreDefinedValue] = useState('');
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);

  useEffect(() => {
    const savedTab = parseInt(localStorage.getItem('tab')) || 0;
    setValue(savedTab);

    if (savedTab === 1 && !isInitialLoaded) {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      setStartValue(firstDayOfMonth);
      setEndValue(today);

      const queryStart = new Date(firstDayOfMonth);
      queryStart.setHours(0, 0, 0, 0);
      const queryEnd = new Date(today);
      queryEnd.setHours(23, 59, 59, 99);

      fetch(
        apiUrl(
          `/timesheet?startdate=${queryStart.toISOString()}&enddate=${queryEnd.toISOString()}&empid=${empId}`
        ),
        {
          method: 'GET',
          credentials: 'include',
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.data === undefined || data.data.length === 0) {
            setTimesheetList(data.data || []);
          } else if (data.data[data.data.length - 1]['checkout'] === null) {
            data.data.splice(-1, 1);
            setTimesheetList(data.data);
          } else {
            setTimesheetList(data.data);
          }
          setIsInitialLoaded(true);
        })
        .catch((err) => console.log(err));
    }
  }, [empId, value, isInitialLoaded]);

  // GET CURRENT CUTOFF TIMESHEET
  useEffect(() => {
    fetch(apiUrl('/timesheet?cutoff=true&empid=' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setTimesheetCutoff(data.data.reverse());
      });
  }, [empId]);

  // GET CURRENT MONTH TIMESHEET
  const fetchMonthTimesheet = (start, end) => {
    const queryStart = new Date(start);
    queryStart.setHours(0, 0, 0, 0);

    const queryEnd = new Date(end);
    queryEnd.setHours(23, 59, 59, 99);

    fetch(
      apiUrl(
        `/timesheet?startdate=${queryStart.toISOString()}&enddate=${queryEnd.toISOString()}&empid=${empId}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.data === undefined || data.data.length === 0) {
          setTimesheetList(data.data);
        } else if (data.data[data.data.length - 1]['checkout'] === null) {
          data.data.splice(-1, 1);
          setTimesheetList(data.data);
        } else {
          setTimesheetList(data.data);
        }
      })
      .catch((err) => console.log(err));
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    localStorage.setItem('tab', newValue);
  };

  const handleCustomDate = () => {
    if (!startValue || !endValue) return;

    // Set hours of custom date range for start and end date when displaying timesheet
    startValue.setHours(0, 0, 0, 0);
    endValue.setHours(23, 59, 59, 99);

    // CONVERT TIME QUERY IN ISOSTRING TO OFFSET TIMEZONE
    const startValueWithTimeZoneOffset = startValue.toISOString();
    const endValueWithTimeZoneOffset = endValue.toISOString();

    fetch(
      apiUrl(
        `/timesheet?startdate=${startValueWithTimeZoneOffset}&enddate=${endValueWithTimeZoneOffset}&empid=${empId}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((data) => {
        // Skip checkout code if array is empty to prevent error throwing
        if (data.data === undefined || data.data.length === 0) {
          setTimesheetList(data.data);
        } else if (data.data[data.data.length - 1]['checkout'] === null) {
          // Remove the timesheet with null check out
          data.data.splice(-1, 1);
          setTimesheetList(data.data);
        } else {
          setTimesheetList(data.data);
        }
        setPickerModalOpen(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleDateChange = (e) => {
    let val = e.target.value;
    setPreDefinedValue(val); // value of drop down
    const today = new Date();

    if (val === 'currentMonth') {
      setStartValue(startOfMonth(today));
      setEndValue(endOfMonth(today));
      return;
    }

    if (val === 'previousCutoff') {
      fetch(apiUrl('/cutoff?active=true'), { method: 'GET', credentials: 'include' })
        .then((res) => res.json())
        .then((resData) => {
          if (resData.success && resData.data.length > 0) {
            const rules = resData.data[0];
            const sA = parseInt(rules.startdayA);
            const eA = parseInt(rules.enddayA);
            const sB = parseInt(rules.startdayB);
            const eB = parseInt(rules.enddayB);

            const today = new Date();
            let currentYear = today.getFullYear();
            let currentMonth = today.getMonth();
            let currentDay = today.getDate();

            let startCutoff, endCutoff;

            let currentBracket = '';
            if (currentDay >= sB && currentDay <= eB) {
              currentBracket = 'B';
            } else {
              currentBracket = 'A';
            }

            if (val === 'previousCutoff') {
              if (currentBracket === 'B') {
                if (currentDay < sA) {
                  currentMonth = currentMonth - 1;
                }
                currentBracket = 'A';
              } else {
                if (currentDay >= sA) {
                  currentBracket = 'B';
                } else {
                  currentMonth = currentMonth - 1;
                  currentBracket = 'B';
                }
              }
            }

            if (currentBracket === 'B') {
              startCutoff = new Date(currentYear, currentMonth, sB);
              endCutoff = new Date(currentYear, currentMonth, eB);
            } else {
              if (currentDay >= sA || val === 'previousCutoff') {
                let anchorDate = new Date(currentYear, currentMonth, sA);
                startCutoff = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), sA);
                endCutoff = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + 1, eA);
              } else {
                startCutoff = new Date(currentYear, currentMonth - 1, sA);
                endCutoff = new Date(currentYear, currentMonth, eA);
              }
            }

            setStartValue(startCutoff);
            setEndValue(endCutoff);
          }
        })
        .catch((err) => console.log("Error querying cutoff parameters: ", err));
      return;
    }

    const threeMonths = subMonths(today, 3); // 3 months before today
    const sixMonths = subMonths(today, 6);
    const oneYear = subYears(today, 1);
    const twoYears = subYears(today, 2);

    setEndValue(today);
    val === '3months'
      ? setStartValue(threeMonths)
      : val === '6months'
        ? setStartValue(sixMonths)
        : val === '1year'
          ? setStartValue(oneYear)
          : setStartValue(twoYears);
  };

  const changeCutoffDetails = () => {
    // console.log(timesheetCutoff[timesheetCutoff.length-1]);

    // if(timesheetCutoff[timesheetCutoff.length-1].checkout == null) {
    //   timesheetCutoff[timesheetCutoff.length-1].checkout == new Date(null);
    // }
    return timesheetCutoff;
  };

  return (
    <PageContainer selectedPage={1}>
      <AppBar position="static">
        <Tabs
          value={value}
          onChange={(e, newValue) => {
            handleChange(e, newValue);
          }}
          indicatorColor="secondary"
          textColor="inherit"
          variant="standard"
        >
          <Tab sx={{ minWidth: '16rem' }} label="Cutoff" />
          <Tab sx={{ minWidth: '16rem' }} label="Timesheet" />
        </Tabs>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {value === 0 ? (
          <CutOffTable data={changeCutoffDetails()} />
        ) : (
          <TimesheetTable
            data={timesheetList}
            cutoff={timesheetCutoff}
            primaryButton={() => {
              setPickerModalOpen(true);
            }}
            entryPerPage={18}
          />
        )}
      </Box>
      <CustomRangeModal
        open={pickerModalOpen}
        onClose={() => {
          setPickerModalOpen(false);
        }}
        startValue={startValue}
        startOnChange={(newValue) => {
          setStartValue(newValue);
          setPreDefinedValue('');
        }}
        endValue={endValue}
        endOnChange={(newValue) => {
          setEndValue(newValue);
          setPreDefinedValue('');
        }}
        primaryClick={handleCustomDate}
        secondaryClick={() => setPickerModalOpen(false)}
        dateRange={preDefinedValue}
        handleDateRangeChange={handleDateChange}
        showOptions
      />
    </PageContainer>
  );
};
